import './StickyFilters.css';

import { useEffect, useState } from 'react';

const StickyFilters = ({
  isVisible,
  filters,
  setFilters,
  nutrientFilters,
  toggleNutrientFilter,
  clearAllFilters,
  totalItems,
  filteredItems,
  sortBy,
  setSortBy,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  // Track pending (unapplied) filters
  const [pendingFilters, setPendingFilters] = useState(filters);
  const [pendingNutrientFilters, setPendingNutrientFilters] = useState(nutrientFilters);
  const [pendingSortBy, setPendingSortBy] = useState(sortBy);

  // Update pending filters when applied filters change (for external changes)
  useEffect(() => {
    setPendingFilters(filters);
  }, [filters]);

  useEffect(() => {
    setPendingNutrientFilters(nutrientFilters);
  }, [nutrientFilters]);

  useEffect(() => {
    setPendingSortBy(sortBy);
  }, [sortBy]);

  const hasActiveFilters =
    Object.values(filters).some(Boolean) || Object.values(nutrientFilters).some(v => v.length > 0);

  const hasPendingChanges = () => {
    const filtersChanged = JSON.stringify(pendingFilters) !== JSON.stringify(filters);
    const nutrientFiltersChanged = JSON.stringify(pendingNutrientFilters) !== JSON.stringify(nutrientFilters);
    const sortChanged = pendingSortBy !== sortBy;
    return filtersChanged || nutrientFiltersChanged || sortChanged;
  };

  const getActiveFiltersCount = () => {
    const dietaryCount = Object.values(filters).filter(Boolean).length;
    const nutrientCount = Object.values(nutrientFilters).filter(levels => levels.length > 0).length;
    return dietaryCount + nutrientCount;
  };

  const activeFiltersCount = getActiveFiltersCount();

  const handleToggleDropdown = () => {
    setShowDropdown(prev => !prev);
  };

  const handleApplyFilters = () => {
    setFilters(pendingFilters);
    setSortBy(pendingSortBy);

    // Apply nutrient filters
    Object.keys(pendingNutrientFilters).forEach(nutrientKey => {
      const currentLevels = nutrientFilters[nutrientKey];
      const pendingLevels = pendingNutrientFilters[nutrientKey];

      // Clear current levels and add pending levels
      currentLevels.forEach(level => {
        if (!pendingLevels.includes(level)) {
          toggleNutrientFilter(nutrientKey, level);
        }
      });

      pendingLevels.forEach(level => {
        if (!currentLevels.includes(level)) {
          toggleNutrientFilter(nutrientKey, level);
        }
      });
    });

    setShowDropdown(false);
  };

  const handleClearAllFilters = () => {
    clearAllFilters();
    setPendingFilters({
      vegetarian: false,
      vegan: false,
      glutenFree: false,
      dairyFree: false,
    });
    setPendingNutrientFilters({
      cereals_containing_gluten: [],
      crustaceans: [],
      eggs: [],
      fish: [],
      peanuts: [],
      soybeans: [],
      milk: [],
      nuts: [],
      celery: [],
      mustard: [],
      sesame_seeds: [],
      sulphur_dioxide_and_sulphites: [],
      lupin: [],
      molluscs: [],
    });
    setPendingSortBy('menu_order');
  };

  const handlePendingNutrientFilter = (nutrientKey, level) => {
    setPendingNutrientFilters(prev => ({
      ...prev,
      [nutrientKey]: prev[nutrientKey].includes(level)
        ? prev[nutrientKey].filter(l => l !== level)
        : [...prev[nutrientKey], level],
    }));
  };

  if (!isVisible) return null;

  return (
    <div className="sticky-filters">
      <div className="sticky-filters-content">
        <div className="filters-trigger" onClick={handleToggleDropdown}>
          <span className="filters-icon">⚙️</span>
          <span className="filters-text">Filters</span>
          {activeFiltersCount > 0 && <span className="filters-count">{activeFiltersCount}</span>}
          <span className="dropdown-arrow">{showDropdown ? '▲' : '▼'}</span>
        </div>

        <div className="results-count">
          {filteredItems} of {totalItems} items
        </div>
      </div>

      {showDropdown && (
        <div className="filters-dropdown">
          <div className="filters-dropdown-content">
            <div className="filter-section">
              <div className="filter-section-header">
                <h4 className="filter-section-title">📊 Sort By</h4>
                {hasActiveFilters && (
                  <button onClick={handleClearAllFilters} className="clear-all-filters-inline">
                    Clear all
                  </button>
                )}
                <button
                  onClick={handleApplyFilters}
                  className={`apply-filters-btn-inline ${hasPendingChanges() ? 'has-changes' : ''}`}
                  disabled={!hasPendingChanges()}
                >
                  Apply Filters
                </button>
              </div>
              <div className="sort-options">
                <select value={pendingSortBy} onChange={e => setPendingSortBy(e.target.value)} className="sort-select">
                  <option value="menu_order">Menu Order</option>
                  <option value="nutri_score">Nutri Score</option>
                  <option value="calories_low">Calories (Low to High)</option>
                  <option value="calories_high">Calories (High to Low)</option>
                  <option value="name">Name (A-Z)</option>
                </select>
              </div>
            </div>

            <div className="filter-section">
              <h4 className="filter-section-title">🥬 Dietary Preferences</h4>
              <div className="dietary-chips">
                <label className={`filter-chip ${pendingFilters.vegetarian ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={pendingFilters.vegetarian}
                    onChange={e => setPendingFilters(prev => ({ ...prev, vegetarian: e.target.checked }))}
                  />
                  <span>🥬 Vegetarian</span>
                </label>
                <label className={`filter-chip ${pendingFilters.vegan ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={pendingFilters.vegan}
                    onChange={e => setPendingFilters(prev => ({ ...prev, vegan: e.target.checked }))}
                  />
                  <span>🌱 Vegan</span>
                </label>
              </div>
            </div>

            {/* <div className="filter-section">
              <h4 className="filter-section-title">🧪 Nutrient Levels</h4>
              <div className="nutrient-filters-grid">
                {[
                  { key: 'protein', label: 'Protein' },
                  { key: 'fiber', label: 'Fiber' },
                  { key: 'sodium', label: 'Sodium' },
                  { key: 'calcium', label: 'Calcium' },
                  { key: 'iron', label: 'Iron' },
                  { key: 'potassium', label: 'Potassium' },
                  { key: 'trans_fat', label: 'Trans Fat' },
                  { key: 'cholesterol', label: 'Cholesterol' },
                  { key: 'added_free_sugars', label: 'Added Sugars' },
                  { key: 'carbohydrates', label: 'Carbohydrates' },
                  { key: 'purines', label: 'Purines' },
                  { key: 'oxalates', label: 'Oxalates' },
                  { key: 'folate', label: 'Folate' },
                ].map(nutrient => (
                  <div key={nutrient.key} className="nutrient-filter-item">
                    <span className="nutrient-label">{nutrient.label}</span>
                    <div className="nutrient-levels">
                      {['low', 'medium', 'high'].map(level => (
                        <button
                          key={level}
                          className={`level-btn ${
                            pendingNutrientFilters[nutrient.key].includes(level) ? 'active' : ''
                          } level-${level}`}
                          onClick={() => handlePendingNutrientFilter(nutrient.key, level)}
                          title={`${level.charAt(0).toUpperCase() + level.slice(1)} level`}
                        >
                          {level === 'low' ? '🟢' : level === 'medium' ? '🟡' : '🔴'}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default StickyFilters;
